export type IntakeOption = { id: string; label: string };

export type IntakeQuestion = {
  id: 'q1' | 'q2' | 'q3';
  text: string;
  hint: string;
  multi: boolean;
  options: IntakeOption[];
};

export const INTAKE_Q1: IntakeQuestion = {
  id: 'q1',
  text: 'Which of these sounds most like you and how you work with AI?',
  hint: "Pick the closest one. There's no wrong answer.",
  multi: false,
  options: [
    {
      id: 'learned',
      label:
        "I know there's a lot I could do with AI, and I've learned plenty, but nothing much has shifted in my business",
    },
    {
      id: 'wary',
      label:
        'I have started shifting my business to leverage AI and I see possibilities, but I am wary of the risks',
    },
    { id: 'sharp', label: 'I use AI across most of my work, and I want to get sharper with it' },
    { id: 'other', label: 'Other' },
  ],
};

export const INTAKE_Q2: IntakeQuestion = {
  id: 'q2',
  text: "What's changed for you in the last year or so?",
  hint: 'Select all that apply.',
  multi: true,
  options: [
    { id: 'standout', label: "It's harder to stand out than it used to be" },
    { id: 'expect', label: 'Clients expect more for the same fee than they used to' },
    { id: 'askai', label: 'People ask me whether I use AI, or how I use it' },
    { id: 'aifirst', label: 'Some prospects try AI first and come to me later, if at all' },
    { id: 'selling', label: "What used to sell isn't selling the way it did" },
    { id: 'none', label: 'Honestly, not much has changed yet' },
  ],
};

export const INTAKE_Q3: IntakeQuestion = {
  id: 'q3',
  text: 'If one thing moved in the next 90 days, what would it be?',
  hint: 'Pick the one that would matter most.',
  multi: false,
  options: [
    { id: 'offer', label: 'What I offer would click with the people I want to reach' },
    { id: 'convert', label: 'More of my conversations would turn into clients' },
    { id: 'prospects', label: "I'd have an easier time finding the right prospects" },
    { id: 'delivery', label: 'Delivery would take less out of me without dropping the quality' },
    { id: 'ontop', label: "I'd feel on top of my business instead of behind it" },
  ],
};

export const INTAKE_QUESTIONS = [INTAKE_Q1, INTAKE_Q2, INTAKE_Q3];

export const INTAKE_Q1_IDS = ['learned', 'wary', 'sharp', 'other'] as const;
export const INTAKE_Q2_IDS = ['standout', 'expect', 'askai', 'aifirst', 'selling', 'none'] as const;
export const INTAKE_Q3_IDS = ['offer', 'convert', 'prospects', 'delivery', 'ontop'] as const;

export type IntakeQ1 = (typeof INTAKE_Q1_IDS)[number];
export type IntakeQ2 = (typeof INTAKE_Q2_IDS)[number];
export type IntakeQ3 = (typeof INTAKE_Q3_IDS)[number];

export type IntakeAnswers = {
  q1: IntakeQ1;
  q1Other?: string;
  q2: IntakeQ2[];
  q3: IntakeQ3;
};

export const INTAKE_OTHER_MAX = 300;
export const INTAKE_MAX_SHOWS = 3;
export const INTAKE_CARD_DAYS = 21;

export type IntakePick = { meetingId: UUID; reason: string };

export type IntakeRecording = {
  meetingId: UUID;
  title: string;
  scheduledAt: Date;
  reason: string;
};

export type IntakeNextCall = {
  meetingId: UUID;
  title: string;
  scheduledAt: Date;
  description: string;
};

export type IntakeResults = {
  recordings: IntakeRecording[];
  upcoming?: IntakeRecording;
  nextCall?: IntakeNextCall;
};

export type IntakeMode = 'off' | 'none' | 'popup' | 'card' | 'done';

export function intakeOptionLabel(question: IntakeQuestion, id: string) {
  return question.options.find((o) => o.id === id)?.label ?? id;
}

export function intakeGoalPhrase(q3: IntakeQ3) {
  const phrases: Record<IntakeQ3, string> = {
    offer: 'making your offer click with the people you want to reach',
    convert: 'turning more of your conversations into clients',
    prospects: 'finding the right prospects more easily',
    delivery: 'delivering with less strain and the same quality',
    ontop: 'getting on top of your business',
  };
  return phrases[q3];
}

export function meetingDisplayTitle(title: string) {
  return title.replace(/^\s*\[[^\]]*\]\s*/, '').trim();
}
